import {
  type AggregateFunction,
  type AggregateFunctionName,
  type ComparisonOperator,
  type DisplayInfo,
  type PageParameter,
  type ParameterInfo,
  type TagItemInfo,
  type VariableInfo,
  type WorkflowVariableDefinitionInfo,
} from '@blaze-shared/api-contracts-domain';
import { type Formula } from '@blaze-shared/formulas-data-access';
import { type UrlVariable } from '@blaze-shared/projects-domain';
import {
  type BaseSchema,
  type BlazeSchema,
  type ColumnType,
  type DatasetColumn,
  type PageContextSchema,
  type VarAccount,
  type VarUser,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';
import { type Workflow, type WorkflowNode } from '@blaze-shared/workflow-domain';

import { type ContextValue } from './intelli-autocomplete.models';
import { type IntellisenseError } from './intellisense-error';

export enum AutocompleteCategory {
  Fixed = 'fixed',
  FormulasCategories = 'formulas-categories',
  FormulasList = 'formulas-list',
  Main = 'main',
  NestedObject = 'nested-object',
  Predefined = 'predefined',
}

export interface AutocompleteConfig {
  accountColumns?: DatasetColumn[] | null;
  column?: DatasetColumn | null;
  /** Preferred datatype if column not specified */
  columnTypes?: ColumnType[];
  context?: IntelliContext;
  /** Columns for $$currentRow object used in the ForEach node. */
  currentRowColumns?: DatasetColumn[] | null;
  /** Columns added inside ForEach node by nodes within that node. */
  currentRowInnerColumns?: DatasetColumn[] | null;
  datasetColumns?: DatasetColumn[];
  disableCategoryLabel: boolean;
  formulas: Formula[];
  hideDataTypeLabels: boolean;
  /** Schema of the the $$input1 input */
  inputSchema?: BaseSchema | WorkflowSchema | null;
  namedInputsColumns?: Record<string, DatasetColumn[]>;
  operator?: ComparisonOperator;
  pageContext?: DatasetColumn[] | null;
  predefinedValues?: ContextValue[];
  schemaColumns?: DatasetColumn[] | null;
  /** Tag values read from schema */
  tagValues?: TagItemInfo[];
  urlVars?: UrlVariable[];
  userColumns?: DatasetColumn[] | null;
  /** When true then show fixed values for links */
  showFixedValuesForLinks?: boolean;
  valuesFromColumn?: any[];
  vars?: Partial<WorkflowVariableDefinitionInfo>[];
  /** Workflow node which is being edited */
  workflowNode?: WorkflowNode | null;
}

export interface IntelliConfig {
  accountsSchema?: BlazeSchema;
  autocompleteCategory?: AutocompleteCategory;
  autocompleteWidth?: number;
  /** Column for which we want to show and enter available values */
  column?: DatasetColumn | null;
  /** Preferred column types if column not specified */
  columnTypes?: ColumnType[];
  /** Schema of the dataset which is used by the given node (access to {ColumnName}) */
  datasetSchema?: BlazeSchema | null;
  disableCategoryLabel?: boolean;
  /** Whether use of dataset column references (eg: {colId:xxx__xxx}) should be disabled */
  disableDatasetColumns?: boolean;
  disableFormulas?: boolean;
  /** Disables $$User, $$Account, @@Var, etc */
  disableGlobalVars?: boolean;
  errors?: IntellisenseError[];
  /** Additional schema used to extract fixed values (links, options/tags) */
  fixedValuesSchema?: BlazeSchema;
  /** Forces intellisense to output data in the specified output type. */
  forceOutputType?: OutputType;
  hideDataTypeLabels?: boolean;
  /** Prevents initial formula tooltip from showing up */
  hideFormulaTooltip?: boolean;
  id: string;
  /** Schema of data which will be available as input ($$input1) for the given node */
  inputSchema?: BaseSchema | WorkflowSchema | null;
  isBlazeTableFormulaColumn?: boolean;
  /** Input should always be treated as a formula */
  isFormula?: boolean;
  /** Schemas of named inputs (of a workflow) */
  namedInputsSchemas?: Record<string, BaseSchema | undefined>;
  /** Schema of data that each individual node in the workflow receives. */
  nodeInputSchemasMap?: Map<string, WorkflowSchema>;
  operator?: ComparisonOperator;
  /** Schema of the table that's going to be modified */
  outputSchema?: BlazeSchema | null;
  pageContextSchema: PageContextSchema | null | undefined;
  placeholder?: string;
  /** Static options that should always be displayed at the main level */
  staticOptions?: { value: string; label: string }[];
  usersSchema?: BlazeSchema;
  /** Initial (and current) value */
  valueInfo?: IntelliValueInfo;
  /** Variable definitions read from workflow config */
  varsFromWorkflow?: Partial<VariableInfo>[];
  /** Workflow which is being edited */
  workflow?: Workflow | null;
  /** Workflow node which is being edited */
  workflowNode?: WorkflowNode | null;

  /** Use new intellisense experience */
  newIntelli?: boolean;
  /** When true then show fixed values for links */
  showFixedValuesForLinks?: boolean;
}

export interface AggregateFunctionOption {
  functionType: AggregateFunctionName;
  name: string;
}

export interface ColumnOption {
  tableName: string;
  name: string;
}

export interface DialogOption {
  id: string;
  name: string;
  mainDatasetId?: string;
}

export interface IntelliContext {
  [key: string]: any;
  /** External account ($Accounts) information */
  account?: VarAccount | null;

  /** input1, dynamic value provided to the context. */
  input?: any;
  /** External user information */
  user?: VarUser | null;
  /** Variables accessible via @@ */
  vars?: WorkflowVariableDefinitionInfo[];
  urlVariables?: UrlVariable[];
}

export interface PageOption {
  id: string;
  isDataView?: boolean;
  mainDatasetId?: string;
  name: string;
  urlParameters?: PageParameter[];
}

/** Option for a UI element */
export interface EvalOption {
  label: string;
  value: string;
  name?: string;
}

export interface QueryOperatorOption {
  label: string;
  value: ComparisonOperator;
}

export interface AggregateFunctionWithGroup extends AggregateFunction {
  /** groupByColumns */
  groupBy: string[];
}

interface ConditionInput {
  /** Keys which point towards the value in the given object */
  keys: string[];
  /** Name of the input (eg. ProjectsTable, input2, Customer) */
  name: string;
  /** Value in the object with "name" found under "keys" */
  value: string | number | boolean;
}

export interface Condition {
  /** Example: `{ ProjectsTable, ['ID'], 'abc123' }` */
  input1: ConditionInput;
  /** Example: `=` */
  operator: string;
  /** Example: `{ Customer, ['ProjectID'], 'def123' }` */
  input2: ConditionInput;
}

export interface InputToken extends DisplayInfo {
  boundryId?: string;
  htmlElement: HTMLDivElement;
  id: string;
  formulaName: string;
}

export interface IntelliError {
  errorMessage: string;
  /** intellisense config ID */
  id: string;
}

/** Information used to help determine what shows in intellisense and what's the value */
export interface IntelliValueInfo {
  displayInfos?: DisplayInfo[];
  isFormulaValue?: boolean;
  value?: string;
}

export interface ParentFormula {
  end?: number;
  formula: Formula;
  inputTokenId?: string;
  parameter?: ParameterInfo | null;
  paramIndex?: number;
  start?: number;
  token: string;
}

export type OutputType =
  | 'ArrayNumbers'
  | 'ArrayStrings'
  | 'Equation'
  | 'Formula'
  | 'JSExpression'
  | 'JSObject'
  | 'String';

export const UNICODE_NBSP = '\u00A0';
/**
 * Used to separate autocomplete values from regular value. This allows us to type regular text after autocomplete
 * token without entering a space.
 */
export const ZERO_WIDTH_SPACE = ' ';

export const NAVIGATION_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

export const RUNTIME_CONTEXT_KEYS = ['$$Account', '$$Page', '$$User', '$$Url', 'storedVars'];
